import { formatExportDate, formatExportCurrency } from './exportHelpers';

const sanitize = (val) => val || '-';
const yesNo = (val) => val ? 'Yes' : 'No';

const buildInfoRow = (label, value) =>
  `<tr><td style="padding:10px 12px;font-weight:600;color:#64748b;width:40%;border-bottom:1px solid #f1f5f9;font-size:13px;">${label}</td>
   <td style="padding:10px 12px;color:#1e293b;border-bottom:1px solid #f1f5f9;font-size:13px;">${value}</td></tr>`;

const buildSection = (title, content) =>
  `<div style="margin-bottom:30px;">
    <h2 style="font-size:18px;color:#0f172a;margin:0 0 12px 0;padding-bottom:8px;border-bottom:2px solid #10b981;">${title}</h2>
    ${content}
  </div>`;

const buildTable = (rows) =>
  `<table style="width:100%;border-collapse:collapse;background:white;border-radius:6px;overflow:hidden;">${rows}</table>`;

export const buildMyDataReport = (data) => {
  const acct = data.account_info || {};
  const reportDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const personalInfo = buildSection('Personal Information', buildTable(
    buildInfoRow('Full Name', sanitize(acct.name))
    + buildInfoRow('Email', sanitize(acct.email))
    + buildInfoRow('Username', sanitize(acct.username))
    + buildInfoRow('Country', sanitize(acct.country))
    + buildInfoRow('Timezone', sanitize(acct.timezone))
    + buildInfoRow('Account Created', formatExportDate(acct.created_at))
    + buildInfoRow('Last Updated', formatExportDate(acct.updated_at))
  ));

  const accountInfo = buildSection('Account & Subscription', buildTable(
    buildInfoRow('Account Type', (acct.subscription_tier || 'premium').charAt(0).toUpperCase() + (acct.subscription_tier || 'premium').slice(1))
    + buildInfoRow('Authentication Method', (acct.auth_type || 'email').charAt(0).toUpperCase() + (acct.auth_type || 'email').slice(1))
    + buildInfoRow('Role', (acct.role || 'user').charAt(0).toUpperCase() + (acct.role || 'user').slice(1))
    + buildInfoRow('Beta Tester', yesNo(acct.is_beta_tester))
    + (acct.beta_code_used ? buildInfoRow('Beta Code', acct.beta_code_used) : '')
    + (acct.beta_expires_at ? buildInfoRow('Beta Access Expires', formatExportDate(acct.beta_expires_at)) : '')
    + buildInfoRow('Onboarding Completed', yesNo(acct.onboarding_completed))
  ));

  const securityInfo = buildSection('Security Settings', buildTable(
    buildInfoRow('Two-Factor Authentication', yesNo(acct.has_2fa))
    + (acct.preferred_2fa_method ? buildInfoRow('2FA Method', acct.preferred_2fa_method) : '')
    + buildInfoRow('Login Alerts', yesNo(acct.login_alerts))
    + buildInfoRow('Strict Security Mode', yesNo(acct.strict_security_mode))
  ));

  let investmentsHtml = '';
  if (data.investments && data.investments.length > 0) {
    const invRows = data.investments.map(inv => {
      const profitLoss = (inv.current_value || inv.amount) - inv.amount;
      const isProfit = profitLoss >= 0;
      return `<tr>
        <td style="padding:10px 12px;border-bottom:1px solid #f1f5f9;font-size:13px;font-weight:600;">${inv.name || '-'}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #f1f5f9;font-size:13px;">${inv.type || '-'}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #f1f5f9;font-size:13px;text-align:right;">${formatExportCurrency(inv.amount, inv.currency)}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #f1f5f9;font-size:13px;text-align:right;">${formatExportCurrency(inv.current_value || inv.amount, inv.currency)}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #f1f5f9;font-size:13px;text-align:right;color:${isProfit ? '#10b981' : '#ef4444'};font-weight:600;">${isProfit ? '+' : ''}${formatExportCurrency(profitLoss, inv.currency)}</td>
      </tr>`;
    }).join('');
    investmentsHtml = buildSection(`Investments (${data.investments.length})`,
      `<table style="width:100%;border-collapse:collapse;background:white;">
        <thead><tr>
          <th style="padding:10px 12px;background:#0f172a;color:white;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.5px;">Name</th>
          <th style="padding:10px 12px;background:#0f172a;color:white;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.5px;">Type</th>
          <th style="padding:10px 12px;background:#0f172a;color:white;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:.5px;">Invested</th>
          <th style="padding:10px 12px;background:#0f172a;color:white;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:.5px;">Current Value</th>
          <th style="padding:10px 12px;background:#0f172a;color:white;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:.5px;">Profit/Loss</th>
        </tr></thead><tbody>${invRows}</tbody></table>`
    );
  } else {
    investmentsHtml = buildSection('Investments', '<p style="color:#94a3b8;font-size:14px;padding:12px 0;">No investments recorded yet.</p>');
  }

  let loginHistoryHtml = '';
  if (data.login_history && data.login_history.length > 0) {
    const loginRows = data.login_history.slice(0, 20).map(log => {
      const agent = log.user_agent || '-';
      const device = agent.includes('Mobile') ? 'Mobile' : agent.includes('Tablet') ? 'Tablet' : 'Desktop';
      const browser = agent.match(/(Chrome|Firefox|Safari|Edge|Samsung)/)?.[1] || 'Unknown';
      return `<tr>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;font-size:12px;">${formatExportDate(log.timestamp)}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;font-size:12px;">${device} / ${browser}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;font-size:12px;">${log.ip_address || '-'}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;font-size:12px;color:${log.success ? '#10b981' : '#ef4444'};font-weight:600;">${log.success ? 'Successful' : 'Failed'}</td>
      </tr>`;
    }).join('');
    loginHistoryHtml = buildSection(`Login History (last ${Math.min(data.login_history.length, 20)})`,
      `<table style="width:100%;border-collapse:collapse;background:white;">
        <thead><tr>
          <th style="padding:8px 12px;background:#0f172a;color:white;text-align:left;font-size:11px;text-transform:uppercase;">Date</th>
          <th style="padding:8px 12px;background:#0f172a;color:white;text-align:left;font-size:11px;text-transform:uppercase;">Device</th>
          <th style="padding:8px 12px;background:#0f172a;color:white;text-align:left;font-size:11px;text-transform:uppercase;">IP Address</th>
          <th style="padding:8px 12px;background:#0f172a;color:white;text-align:left;font-size:11px;text-transform:uppercase;">Status</th>
        </tr></thead><tbody>${loginRows}</tbody></table>`
    );
  } else {
    loginHistoryHtml = buildSection('Login History', '<p style="color:#94a3b8;font-size:14px;padding:12px 0;">No login history recorded.</p>');
  }

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>My Data - MyInvestFolio</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;padding:40px;color:#1e293b;line-height:1.6;background:#f8fafc}
.container{max-width:800px;margin:0 auto;background:white;padding:40px;border-radius:12px;box-shadow:0 1px 3px rgba(0,0,0,0.1)}
.header{border-bottom:3px solid #10b981;padding-bottom:20px;margin-bottom:30px}
.header h1{font-size:28px;color:#0f172a;margin-bottom:4px}
.header .subtitle{color:#64748b;font-size:13px}
.footer{margin-top:40px;padding-top:20px;border-top:1px solid #e2e8f0;text-align:center;color:#94a3b8;font-size:11px}
.notice{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:16px;margin-bottom:30px;font-size:13px;color:#166534}
@media print{body{padding:10px;background:white}.container{box-shadow:none;padding:20px}.no-print{display:none}}
</style></head><body>
<div class="container">
  <div class="header">
    <h1>MyInvestFolio</h1>
    <p class="subtitle">Personal Data Export - ${reportDate}</p>
  </div>
  <div class="notice">
    This document contains all personal data held by MyInvestFolio for your account.
    Generated on ${new Date().toLocaleString('en-US')} at your request.
  </div>
  ${personalInfo}
  ${accountInfo}
  ${securityInfo}
  ${investmentsHtml}
  ${loginHistoryHtml}
  <div class="footer">
    <p><strong>MyInvestFolio</strong> - Private Portfolio Infrastructure</p>
    <p style="margin-top:4px;">This is a confidential document. Handle with care.</p>
  </div>
</div>
<div class="no-print" style="text-align:center;margin:20px;">
  <button onclick="window.print()" style="background:#10b981;color:white;border:none;padding:12px 32px;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;">Save as PDF</button>
</div>
</body></html>`;
};
